import { Alert } from "@package/subframe/components/Alert";
import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { StaffForm } from "@/components/staff/StaffForm";
import { Spinner } from "@/components/ui/spinner";
import { env } from "@/config/env";
import { useNursery } from "@/contexts/NurseryContext";
import { useListNurseryLineManagerCandidates } from "@/generated/people/people";
import { isProfileUnavailable } from "@/lib/people";

/** While the People Hub is switched off, the old Add Staff form is the live page. */
export const Route = createFileRoute("/_authenticated/people/new")({
  beforeLoad: () => {
    if (!env.VITE_PEOPLE_HUB_ENABLED) {
      throw redirect({ to: "/settings/staff/new" });
    }
  },
  component: AddEmployeePage,
});

/**
 * The route guard lets everyone into /people, so the page asks the server
 * instead: the line manager list is a 404 for anyone who may not add
 * employees at the selected nursery.
 */
function AddEmployeePage() {
  const navigate = useNavigate();
  const { selectedNurseryId, isLoading: isNurseryLoading } = useNursery();
  const access = useListNurseryLineManagerCandidates(selectedNurseryId ?? "", {
    // Kept while another nursery's answer loads, so switching nursery in the
    // sidebar doesn't unmount the form and lose what was typed.
    query: { enabled: !!selectedNurseryId, placeholderData: keepPreviousData },
  });
  const noNursery = !isNurseryLoading && !selectedNurseryId;
  const refused = noNursery || isProfileUnavailable(access.error);

  useEffect(() => {
    if (!refused) {
      return;
    }
    navigate({ to: "/people", replace: true });
  }, [refused, navigate]);

  if (access.isSuccess) {
    return <StaffForm />;
  }

  if (access.isPaused) {
    return (
      <Alert
        variant="neutral"
        title="Adding an employee needs a connection"
        description="Reconnect to add them."
      />
    );
  }

  if (access.isError && !refused) {
    return (
      <Alert
        variant="error"
        title="Could not load this page"
        description="Try again in a moment."
      />
    );
  }

  return (
    <div className="flex w-full items-center justify-center py-16">
      <Spinner />
    </div>
  );
}
